// engine/preflopHandoff.ts (PF3) - the played-preflop → postflop handoff for the DO modes.
//
// The scaffolding autopilot (scenario/generate.ts `preflopOpenCallAutopilot`) plays a dumb open/call line
// so postflop spots are reachable. This replaces it with hero's REAL played line: at each preflop decision
// it resolves hero's exact combo against the shipped charts (recommendPreflop, PF1) and plays that action,
// so hero's own preflop line - limp / open / 3-bet - becomes the postflop context. Because
// selectVillainStartingRange already reads the villain's line off the HandState, a hero 3-bet (villain
// calls) produces a 3-bet-pot villain range that differs from a limped pot with NO new plumbing (PF3
// done-criteria a; proven in engine/strategy/playedPreflopHandoff.test.ts). It also builds hero's FIRST
// preflop decision as step 0 of the DO-mode stepper (the playable analogue of GH8's Observe recap).
//
// Pure over the injected data (archetype / range-set / rule loaders) + a HandState - no I/O here; the
// caller (liveRunner) wires the loaders from liveData. Kept mode-agnostic so Tournament can reuse it.
import type {
  Action,
  ActionKind,
  Archetype,
  ArchetypeId,
  CardIndex,
  ExploitRule,
  HandState,
  LegalAction,
  Position,
  PositionClass,
  PotType,
  PreflopRecommendation,
  RangeFamily,
  RangeSet,
  RangeShape,
  TableType,
} from '@postflop/schema';
import {
  advance,
  assignPositions,
  createHand,
  createRng,
  dealForSpec,
  deriveHeroPreflopSpot,
  gradePreflopDecision,
  legalActions,
  nextInt,
  postflopOrder,
  recommendPreflop,
  scriptVillainAction,
  type Deal,
  type PreflopContext,
  type PreflopDecisionGrade,
  type PreflopUserDecision,
} from '@postflop/engine';

/** Map a diagnosed table type (read.ts) → the range-set family hero plays (North Star §5 PF1 amendment:
 *  loose-passive/limp-heavy/station live → live_loose; tight → live_tight; standard/aggressive → baseline). */
export const tableTypeFamily = (t: TableType | undefined): RangeFamily => {
  switch (t) {
    case 'loosePassive':
    case 'limpHeavy':
    case 'callingStation':
    case 'deepSplashy':
    case 'promoHeavy':
      return 'live_loose';
    case 'tight':
      return 'live_tight';
    case 'aggressiveReg':
    default:
      return 'baseline';
  }
};

/** The loaders + fixed table facts the preflop context builder needs (no I/O in this module). */
export interface PreflopPlayDeps {
  archetype: (id: ArchetypeId) => Archetype;
  rangeSetOf: (id: string) => RangeSet;
  rules: ExploitRule[];
  /** the range-set family hero plays (mapped from the spec's tableType via tableTypeFamily). */
  tableType: RangeFamily;
  /** chart size (6 or 9 max) - the shipped charts only come in those two. */
  tableSize: 6 | 9;
  /** the primary villain seat (the read is about) - supplies the archetype for rule matching. */
  primaryVillainSeat: number;
  /**
   * TB2: resolve the villain hero ACTUALLY faces, from the live state. `primaryVillainSeat` is a static
   * `spec.villains[0].seat`, which at the 3-handed baseSpec happened to be the only villain in the pot -
   * but at a 7-handed continuous table seat 1 is an arbitrary stranger who has usually folded, so the
   * preflop rules would match the wrong archetype. Supplied by the session path; OMITTED on the baseSpec
   * path, which keeps `contextVillain` byte-identical there (GH4/GH7 keys unmoved).
   */
  resolvePrimaryVillain?: (state: HandState) => number | undefined;
}

/** A neutral villain range shape for the preflop rule context. Preflop exploit rules key on the villain's
 *  archetype imbalances + the facing line, NOT on a postflop range shape, so a placeholder is honest here. */
const NEUTRAL_SHAPE: RangeShape = { valueHeavyPct: 50, bluffPct: 10, topClasses: [] };

/** hero IP/OOP for the preflop context: last to act postflop among the live seats ⇒ IP. */
const heroPositionClass = (state: HandState, heroSeat: number): PositionClass => {
  const order = postflopOrder(state.players.length, state.buttonSeat);
  const live = order.filter((seat) => {
    const p = state.players.find((pp) => pp.seat === seat);
    return p && !p.folded;
  });
  return live[live.length - 1] === heroSeat ? 'IP' : 'OOP';
};

/** The archetype resolved for the preflop rule context - the primary villain if still present, else any
 *  seated villain (a folded seat still carries its archetypeId, which is all rule matching needs). */
const contextVillain = (state: HandState, deps: PreflopPlayDeps): Archetype => {
  // TB2: prefer the villain hero actually faces (session path), then the static primary, then any seated
  // villain. A folded seat still carries its archetypeId, which is all rule matching needs.
  const resolved = deps.resolvePrimaryVillain?.(state);
  const seat = resolved ?? deps.primaryVillainSeat;
  const primary = state.players.find((p) => p.seat === seat);
  const id = primary?.archetypeId ?? state.players.find((p) => p.seat !== state.spec.heroSeat)?.archetypeId;
  if (!id) throw new Error('preflopHandoff: no villain archetype available for the preflop context');
  return deps.archetype(id);
};

/**
 * Build the PreflopContext recommendPreflop() answers, from HERO's real preflop line off the HandState.
 * deriveHeroPreflopSpot reads the facing line (open / defend / vs-3bet …); the charts + rules come from the
 * injected loaders. Pure over (state, deal, deps).
 */
export const buildHeroPreflopContext = (
  state: HandState,
  heroSeat: number,
  deal: Deal,
  deps: PreflopPlayDeps,
): PreflopContext => {
  const heroCards = deal.holeCards[heroSeat] as [CardIndex, CardIndex];
  const { heroPosition, facing } = deriveHeroPreflopSpot(state, heroSeat);
  const bb = state.spec.stakes.bb;
  const pot = state.pots.reduce((s, p) => s + p.amount, 0);

  const families: Partial<Record<string, RangeSet>> = {};
  for (const t of ['baseline', 'live_loose', 'live_tight'] as RangeFamily[]) {
    const id = `${t}_${deps.tableSize}max`;
    families[id] = deps.rangeSetOf(id);
  }

  return {
    hero: { cards: heroCards, position: heroPosition },
    facing,
    tableType: deps.tableType,
    tableSize: deps.tableSize,
    families,
    villain: { archetype: contextVillain(state, deps), rangeShape: NEUTRAL_SHAPE },
    playersInHand: state.players.filter((p) => !p.folded).length,
    positionClass: heroPositionClass(state, heroSeat),
    potBB: pot / bb,
    rules: deps.rules,
  };
};

/** Legalize a strategy action onto the true legal set (mirrors liveRunner.legalizeAction, kept local so the
 *  preflop autopilot never emits an action the state doesn't offer - e.g. 'raise' when hero can only call). */
const legalizePreflop = (action: ActionKind, legal: LegalAction[]): ActionKind => {
  const kinds = new Set(legal.map((l) => l.kind));
  if (kinds.has(action)) return action;
  const pick = (...cands: ActionKind[]): ActionKind => cands.find((c) => kinds.has(c)) ?? legal[0].kind;
  switch (action) {
    case 'raise':
      return pick('raise', 'bet', 'call', 'check');
    case 'jam':
      return pick('jam', 'raise', 'bet', 'call');
    case 'call':
      return pick('call', 'check', 'fold');
    case 'check':
      return pick('check', 'call', 'fold');
    case 'bet':
      return pick('bet', 'raise', 'call', 'check');
    case 'fold':
      return pick('fold', 'check', 'call');
  }
};

/** Turn a PreflopRecommendation into the concrete Action the hand machine consumes - the recommended
 *  action legalized to the true legal set, with the recommended bb sizing driven to a concrete legal
 *  "to" amount for a raise (clamped to the legal min/max), and the all-in amount for a jam. */
export const mechanicalPreflopAction = (
  rec: PreflopRecommendation,
  state: HandState,
  heroSeat: number,
): Action => {
  const legal = legalActions(state);
  const kind = legalizePreflop(rec.action, legal);
  const bb = state.spec.stakes.bb;

  if (kind === 'raise') {
    const l = legal.find((x) => x.kind === 'raise');
    let to = rec.sizing ? Math.round(rec.sizing.toBB * bb) : (l?.min ?? bb * 2);
    if (l?.min !== undefined) to = Math.max(to, l.min);
    if (l?.max !== undefined) to = Math.min(to, l.max);
    return { seat: heroSeat, kind: 'raise', amount: to };
  }
  if (kind === 'jam') {
    const l = legal.find((x) => x.kind === 'jam');
    return l?.max !== undefined
      ? { seat: heroSeat, kind: 'jam', amount: l.max }
      : { seat: heroSeat, kind: 'jam' };
  }
  return { seat: heroSeat, kind };
};

/**
 * A HeroAutopilot (scenario/generate.ts) that plays hero's REAL preflop line: at each preflop decision it
 * resolves recommendPreflop and plays that action, stopping at hero's first POSTFLOP decision (same halt
 * contract as preflopOpenCallAutopilot). Structurally a HeroAutopilot - assignable without importing the
 * named type. Deterministic given the deal + deps.
 */
export const makeHeroPlayedPreflopAutopilot =
  (deps: PreflopPlayDeps) =>
  (state: HandState, deal: Deal, heroSeat: number): Action | { stop: true } => {
    if (state.street !== 'preflop') return { stop: true };
    const ctx = buildHeroPreflopContext(state, heroSeat, deal, deps);
    const rec = recommendPreflop(ctx);
    return mechanicalPreflopAction(rec, state, heroSeat);
  };

/** The result of resolving hero's FIRST preflop decision - the pieces the step-0 snapshot needs. */
export interface HeroPreflopDecision {
  /** the HandState at hero's first preflop decision (before hero acts). */
  state: HandState;
  ctx: PreflopContext;
  recommendation: PreflopRecommendation;
  role: PreflopRecommendation['role'];
  potType: PotType;
  effectiveStackBB: number;
  toCallBB?: number;
  potBB: number;
  openerPosition?: Position;
  heroPosition: Position;
  positionClass: PositionClass;
  /** grade hero's chosen preflop intent against the recommendation (PF2, outcome-independent). */
  grade: (user: PreflopUserDecision) => PreflopDecisionGrade;
}

/** raises-so-far → potType (matches scenario/spot.ts + strategy/preflop.ts potTypeFor). */
const potTypeFromState = (state: HandState): PotType => {
  const bb = state.spec.stakes.bb;
  const raises = state.history.filter(
    (h) =>
      h.street === 'preflop' &&
      (h.action.kind === 'raise' || (h.action.kind === 'jam' && (h.action.amount ?? 0) > bb)),
  ).length;
  return raises === 0 ? 'limped' : raises === 1 ? 'srp' : raises === 2 ? '3bp' : '4bp';
};

/**
 * Replay `spec` deterministically to hero's FIRST preflop decision and resolve it. Villains are scripted
 * exactly as generate() scripts them (same `${seed}::villain` rng), so the returned decision matches the
 * hand generate() produced under the same (winning) seed. Returns null if hero never faces a preflop
 * decision (folded around to a walk, or an all-fold) - the caller then skips that seed.
 */
export const resolveHeroFirstPreflop = (
  spec: HandState['spec'],
  deps: PreflopPlayDeps,
  archetypeOf: (id: ArchetypeId) => Archetype,
): HeroPreflopDecision | null => {
  const heroSeat = spec.heroSeat;
  const n = spec.villains.length + 1;
  const buttonSeat = resolveButtonForReplay(spec, n);
  const deal = dealForSpec(spec, n);
  const villainRng = createRng(`${spec.seed}::villain`);
  let state = createHand({ handId: `pf:${spec.seed}`, spec, buttonSeat });

  const guard = n * 20;
  for (let i = 0; i < guard; i++) {
    if (state.phase !== 'acting' || state.toAct === null) return null; // hand ended without a hero decision
    if (state.toAct === heroSeat && state.street === 'preflop') break; // hero's first preflop decision
    if (state.street !== 'preflop') return null; // hero's first decision is postflop (never acted preflop)
    const seat = state.toAct;
    const villain = state.players.find((p) => p.seat === seat)!;
    state = advance(state, scriptVillainAction(state, seat, archetypeOf(villain.archetypeId!), deal, villainRng));
  }
  if (state.toAct !== heroSeat || state.street !== 'preflop') return null;

  return resolveHeroPreflopAt(state, deal, deps);
};

/**
 * Resolve hero's preflop decision AT a state he is already to-act on. This is the body
 * `resolveHeroFirstPreflop` used to inline; it is split out because that function REPLAYS from a spec and
 * re-derives the button itself (`resolveButtonForReplay`) with no way to pass carried stacks - both wrong
 * for a continuous table, where the session owns the button and the stacks. TB2's session walk already has
 * the correct state in hand, so it calls this directly and never re-replays.
 *
 * Pure over (state, deal, deps). Caller guarantees `state.toAct === heroSeat && state.street === 'preflop'`.
 */
export const resolveHeroPreflopAt = (
  state: HandState,
  deal: Deal,
  deps: PreflopPlayDeps,
): HeroPreflopDecision => {
  const heroSeat = state.spec.heroSeat;
  const ctx = buildHeroPreflopContext(state, heroSeat, deal, deps);
  const rec = recommendPreflop(ctx);
  const bb = state.spec.stakes.bb;
  const hero = state.players.find((p) => p.seat === heroSeat)!;
  const pot = state.pots.reduce((s, p) => s + p.amount, 0);
  const toCall = Math.max(0, state.currentBet - hero.committedThisStreet);

  return {
    state,
    ctx,
    recommendation: rec,
    role: rec.role,
    potType: potTypeFromState(state),
    effectiveStackBB: ctx.facing.effectiveStackBB,
    toCallBB: toCall > 0 ? toCall / bb : undefined,
    potBB: pot / bb,
    openerPosition: ctx.facing.openerPosition,
    heroPosition: ctx.hero.position,
    positionClass: ctx.positionClass,
    grade: (user: PreflopUserDecision): PreflopDecisionGrade =>
      gradePreflopDecision({ ctx, recommendation: rec, user }),
  };
};

/** Resolve the button seat the same way generate()/playOneDeal does (honor a pinned heroPosition, else the
 *  seed-derived seat). Kept in lock-step with scenario/generate.ts resolveButtonSeat. */
const resolveButtonForReplay = (spec: HandState['spec'], n: number): number => {
  if (spec.heroPosition) {
    for (let b = 0; b < n; b++) {
      if (assignPositions(n, b).get(spec.heroSeat) === spec.heroPosition) return b;
    }
    throw new RangeError(`preflopHandoff: heroPosition '${spec.heroPosition}' unreachable for seat ${spec.heroSeat}`);
  }
  // deterministic seed-derived button (matches generate's nextInt(createRng(`${seed}::setup`), n)).
  return nextInt(createRng(`${spec.seed}::setup`), n);
};
